<template>
  <div id="app" class="h-full overflow-y-hidden bg-redx">
    <!-- Pull-to-refresh indicator -->
    <div
      class="ptr-indicator"
      :class="{
        'ptr-visible': pullDistance > 0,
        'ptr-refreshing': isRefreshing,
      }"
      :style="{ height: Math.min(pullDistance, 80) + 'px' }"
    >
      <div class="ptr-content">
        <div v-if="isRefreshing" class="ptr-spinner-container">
          <div class="ptr-spinner"></div>
          <div class="ptr-text">
            {{ t(["Refreshing...", "ກຳລັງໂຫຼດໃໝ່..."]) }}
          </div>
        </div>
        <div
          v-else
          class="ptr-arrow-container"
          :class="{ 'ptr-arrow-up': pullDistance >= 80 }"
        >
          <div class="ptr-arrow">↓</div>
          <div class="ptr-text">
            {{
              pullDistance >= 80
                ? t(["Release to update", "ປ່ອຍເພື່ອອັບເດດ"])
                : t(["Pull to update", "ດຶງລົງເພື່ອອັບເດດ"])
            }}
          </div>
        </div>
      </div>
    </div>
    <router-view />

    <!-- Floating Reload Button (shown when new version is ready) -->
    <transition name="reload-btn">
      <div
        v-if="showReloadButton"
        class="floating-reload-wrapper"
        @click="onReloadClick"
      >
        <div class="floating-reload-btn">
          <div class="reload-glow"></div>
          <div class="reload-icon-wrap">
            <svg
              class="reload-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="23 4 23 10 17 10" />
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
            </svg>
          </div>
          <span class="reload-label">{{ t(["Update", "ອັບເດດ"]) }}</span>
        </div>
        <div class="reload-badge-pulse"></div>
      </div>
    </transition>
    <!-- Winner Prize Celebration Modal -->
    <WinnerPrizeModal
      :visible.sync="showWinnerPrizeModal"
      :prizeData="winnerPrizeData"
    />
  </div>
</template>

<script>
import { get } from "vuex-pathify";
import forge from "node-forge";
import axios from "axios";
import WinnerPrizeModal from "@/components/WinnerPrizeModal.vue";

export default {
  name: "App",
  components: {
    WinnerPrizeModal,
  },
  computed: {
    user: get("user"),
  },
  data() {
    return {
      publicKey: null,
      privateKey: null,
      machineid: null,
      flutter: null,
      location: null,
      device: null,
      urlParams: null,
      signature: process.env.VUE_APP_API_SIGNATURE || "",
      // Pull-to-refresh state
      pullDistance: 0,
      startY: 0,
      isPulling: false,
      isRefreshing: false,
      // Floating reload button
      showReloadButton: false,
      // Winner Prize Modal state
      showWinnerPrizeModal: false,
      winnerPrizeData: {},
      webversion: null,
    };
  },
  beforeDestroy() {
    const el = this.$el;
    if (el) {
      el.removeEventListener("touchstart", this.onTouchStart);
      el.removeEventListener("touchmove", this.onTouchMove);
      el.removeEventListener("touchend", this.onTouchEnd);
    }
  },
  async mounted() {
    // Setup pull-to-refresh touch listeners
    this.$nextTick(() => {
      const el = this.$el;
      if (el) {
        el.addEventListener("touchstart", this.onTouchStart, { passive: true });
        el.addEventListener("touchmove", this.onTouchMove, { passive: false });
        el.addEventListener("touchend", this.onTouchEnd, { passive: true });
      }
    });

    // Register global handler for Flutter to show reload button
    window.showWebReloadButton = () => {
      this.showReloadButton = true;
      this.onReloadClick();
    };

    this.$store.set("isshowpopup", 1);

    // Register deep link handlers unconditionally at mount time
    window.handleDeepLink = (url) => {
      this.processDeepLink(url);
    };

    window.addEventListener("deep_link", (event) => {
      const { url } = event.detail || {};
      this.processDeepLink(url);
    });

    this.flutter = await this.waitForFlutter();
    if (this.flutter) {
      try {
        const pendingLink = await this.flutter.callHandler(
          "getPendingDeepLink",
        );
        if (pendingLink) {
          this.processDeepLink(pendingLink);
        }
      } catch (e) {
        console.error("Error pulling pending deep link", JSON.stringify(e));
      }

      // Check if native has a pending update to show reload button
      try {
        const shouldReload = await this.flutter.callHandler("shouldShowReload");
        if (shouldReload) {
          this.showReloadButton = true;
          this.onReloadClick();
        }
      } catch (e) {
        console.error("Error checking shouldShowReload", JSON.stringify(e));
      }

      await this.loadKeys();

      try {
        this.device = await this.flutter.callHandler("getdevice");
        this.$store.set("device", this.device);
      } catch (e) {
        console.error("errorgetdevice", JSON.stringify(e));
      }

      try {
        const otpsignature = await this.flutter.callHandler("getotpsignature");
        this.$store.set("otpsignature", otpsignature);
      } catch (error) {
        console.error("errorotpsignature", JSON.stringify(error));
      }

      try {
        this.webversion = await this.flutter.callHandler(
          "getdata",
          "webversion",
        );
      } catch (error) {
        console.error("errorgetdatawebversion", JSON.stringify(error));
      }
    }
    this.machineid = this.$store.get("machineid");
    if (!this.machineid) {
      try {
        if (this.flutter) {
          this.machineid = await this.flutter.callHandler(
            "getdata",
            "machineid",
          );
          if (!this.machineid) {
            await this.registerMachine();
          }
        }
      } catch (error) {
        console.error("errorgetmachineid", JSON.stringify(error));
      }
    }

    if (!localStorage.getItem("app_machine_key")) {
      try {
        if (this.flutter) {
          const machineKey = await this.flutter.callHandler(
            "getdata",
            "machinekey",
          );
          if (machineKey) {
            localStorage.setItem("app_machine_key", machineKey);
          }
        }
      } catch (error) {
        console.error("errorgetmachinekey", JSON.stringify(error));
      }
    }

    try {
      this.flutter
        .callHandler("getlocation")
        .then((loc) => {
          this.location = loc;
          if (this.location) {
            this.$store.set("location", this.location);
          }
        })
        .catch((e) => {
          console.error("errorgetlocation", JSON.stringify(e));
        });
    } catch (error) {
      console.error("error getlocation", JSON.stringify(error));
    }

    // this.checkRecentWinnerPrize();
  },
  methods: {
    async checkRecentWinnerPrize() {
      if (!this.user || !this.user.access_token) return;
      try {
        axios
          .post("/api/WinnerPrize/getRecentWinnumberByAppUser", {})
          .then((res) => {
            if (res) {
              if (!res.didError) {
                const model = res["model"];
                if (model && model.isShowable) {
                  this.winnerPrizeData = model;
                  this.showWinnerPrizeModal = true;
                }
              }
            }
          });
      } catch (error) {
        console.error("Errorcheckingrecentwinnerprize:", JSON.stringify(error));
      }
    },
    onReloadClick() {
      this.showReloadButton = false;
      const flutter =
        this.flutter ||
        (window.flutter_inappwebview ? window.flutter_inappwebview : null);
      if (flutter) {
        flutter.callHandler("reload");
      } else {
        window.location.reload();
      }
    },
    onTouchStart(e) {
      if (this.isRefreshing) return;
      this.startY = e.touches[0].clientY;
      this.isPulling = false;
    },
    onTouchMove(e) {
      if (this.isRefreshing) return;
      const currentY = e.touches[0].clientY;
      const diff = currentY - this.startY;
      // Only activate pull-to-refresh when at the top of the page
      const scrollables = this.$el.querySelectorAll(
        '[class*="overflow-y-scroll"], [class*="overflow-scroll"]',
      );
      let isAtTop = true;
      scrollables.forEach((el) => {
        if (el.scrollTop > 0) isAtTop = false;
      });
      if (window.scrollY > 0) isAtTop = false;
      if (diff > 0 && isAtTop) {
        this.isPulling = true;
        this.pullDistance = Math.min(diff * 0.5, 120);
        if (this.pullDistance > 5) {
          e.preventDefault();
        }
      }
    },
    onTouchEnd() {
      if (this.isRefreshing) return;
      if (this.isPulling && this.pullDistance >= 80) {
        this.isRefreshing = true;
        this.pullDistance = 60;
        try {
          const flutter = this.flutter || this.getflutter();
          if (flutter) {
            flutter.callHandler(
              "checkForUpdate",
              `https://api.winsuperwin.com:6345/api/StandardData/getAppVersionNew2`,
            );
          } else {
            // Fallback: reload the page
            window.location.reload();
          }
        } catch (err) {
          console.error("Pull-to-refresh error", JSON.stringify(err));
          window.location.reload();
        }
        // Reset after a short delay
        setTimeout(() => {
          this.isRefreshing = false;
          this.pullDistance = 0;
        }, 1500);
      } else {
        this.pullDistance = 0;
      }
      this.isPulling = false;
    },
    waitForFlutter() {
      if (window.flutter_inappwebview) {
        return Promise.resolve(window.flutter_inappwebview);
      }
      return new Promise((resolve) => {
        let count = 0;
        const interval = setInterval(() => {
          count++;
          if (window.flutter_inappwebview) {
            clearInterval(interval);
            resolve(window.flutter_inappwebview);
          } else if (count >= 50) {
            clearInterval(interval);
            resolve(null);
          }
        }, 100);
      });
    },
    async processDeepLink(url) {
      if (typeof url === "string") {
        if (url.startsWith('"') && url.endsWith('"')) {
          try {
            url = JSON.parse(url);
          } catch (e) {
            console.warn("Error parsing double-quoted URL string:", e);
          }
        }
      }
      if (!this.flutter) {
        this.flutter = await this.waitForFlutter();
      }
      if (this.flutter) {
        if (!this.device) {
          try {
            this.device = await this.flutter.callHandler("getdevice");
            if (this.device) {
              this.$store.set("device", this.device);
            }
          } catch (e) {
            console.error("error getdevice in deeplink", JSON.stringify(e));
          }
        }

        if (!this.privateKey || !this.publicKey) {
          try {
            await this.loadKeys();
          } catch (error) {
            console.error("error loadKeys in deeplink", JSON.stringify(error));
          }
        }
      }

      // Safe parsing of query parameters using URLSearchParams
      let searchParams = null;
      try {
        const qIdx = url.indexOf("?");
        const searchParamsString = qIdx !== -1 ? url.substring(qIdx) : "";
        searchParams = new URLSearchParams(searchParamsString);
      } catch (e) {
        console.error("ErrorcreatingURLSearchParams", JSON.stringify(e));
      }

      try {
        this.urlParams = searchParams;
        let ticket = "";
        if (searchParams) {
          ticket = searchParams.get("ticket");
          if (ticket) {
            this.$swal.showLoading();
            const req = { ticket };
            if (this.device) {
              req["appVersion"] =
                this.webversion ?? this.device["deviceversion"] ?? "";
              req["devicePlatform"] = this.device["devicetype"];
              req["deviceName"] = this.device["devicename"];
            } else {
              req["appVersion"] = `${this.webversion ?? ""}`;
              req["devicePlatform"] = "";
              req["deviceName"] = "";
            }

            if (this.location) {
              req["deviceLocation"] = `${this.location["latitude"] ?? ""},${
                this.location["longitude"] ?? ""
              }`;
              req["lat"] = Number(this.location["latitude"] ?? 0);
              req["lng"] = Number(this.location["longitude"] ?? 0);
            } else {
              req["deviceLocation"] = "";
              req["lat"] = 0;
              req["lng"] = 0;
            }
            axios
              .post("/api/Users/exchangeDeeplinkTicket", req)
              .then((res) => {
                this.$swal.close();
                if (res) {
                  if (res["didError"]) {
                    this.$swal.fire("", res.message, "info");
                    return;
                  }
                  if (res["model"]) {
                    this.loginSuccess(res["model"]);
                    return;
                  }
                }
              })
              .catch((e) => {
                this.$swal.close();
                console.error("errorexchangdeeplinkticket", JSON.stringify(e));
              });
          }
        }
      } catch (error) {
        this.$swal.close();
        console.error("errorexchangdeeplinkticket2", JSON.stringify(error));
      }
    },
    closeNative() {
      console.log("CLOSENATIVE, GOODBYE SEE YOU LATER");
    },
    async loadKeys() {
      if (this.flutter) {
        try {
          this.privateKey = await this.flutter.callHandler(
            "readsecure",
            "privateKey",
          );
        } catch (error) {
          console.error("errorloadprivatekey", JSON.stringify(error));
          this.privateKey = await this.flutter.callHandler(
            "getdata",
            "privateKey",
          );
        }
        try {
          this.publicKey = await this.flutter.callHandler(
            "readsecure",
            "publicKey",
          );
        } catch (error) {
          console.error("errorloadpublickey", JSON.stringify(error));
          this.publicKey = await this.flutter.callHandler(
            "getdata",
            "publicKey",
          );
        }
      }

      if (!this.privateKey || !this.publicKey) {
        try {
          await this.generateKeys();
        } catch (error) {
          console.error("genkeyerror", JSON.stringify(error));
        }
      }
    },
    async generateKeys() {
      if (!this.publicKey || !this.privateKey) {
        const rsa = forge.pki.rsa;
        const keypair = rsa.generateKeyPair({ bits: 2048, e: 0x10001 });
        this.publicKey = forge.pki.publicKeyToPem(keypair.publicKey);
        this.privateKey = forge.pki.privateKeyToPem(keypair.privateKey);
        this.$store.set("publicKey", this.publicKey);
        this.$store.set("privateKey", this.privateKey);
        if (this.flutter) {
          const req = {};
          req["passkey"] = "-";
          if (this.user && this.user["userOtp"]) {
            req["passkey"] = this.user["userOtp"];
          }
          req["digitalSignature"] = "-";
          req["pubKey"] = this.publicKey;
          req["machineKey"] = this.getmachinekey(this.device);
          if (this.user) {
            req["userId"] = this.user["userid"] ?? 0;
            req["userName"] = this.user["username"] ?? "";
          }

          try {
            await axios.post("/api/Users/RegisterRSA", req);
          } catch (error) {
            console.error("RegisterRSA ERROR", JSON.stringify(error));
          }
          try {
            await this.flutter.callHandler(
              "writesecure",
              "publicKey",
              this.publicKey,
            );
          } catch (error) {
            await this.flutter.callHandler(
              "setdata",
              "publicKey",
              this.publicKey,
            );
          }

          try {
            await this.flutter.callHandler(
              "writesecure",
              "privateKey",
              this.privateKey,
            );
          } catch (error) {
            await this.flutter.callHandler(
              "setdata",
              "privateKey",
              this.privateKey,
            );
          }
        }
      }
    },
    async registerMachine() {
      const req = {
        machineId: this.machineid ? this.machineid : 0,
        status: 0,
        userId: 0,
      };
      req["machineName"] = this.device["devicename"];
      req["machineKey"] = this.getmachinekey(this.device);
      req["publicKey"] = this.publicKey;
      const r = await axios.post("/api/Users/registerMachine", req);

      if (!r["didError"]) {
        if (r["model"]) {
          this.machineid = r["model"]["machineId"];
          this.$store.set("machineid", this.machineid);
          await this.flutter.callHandler(
            "setdata",
            "machineid",
            `${this.machineid}`,
          );
        }
      }
    },
    async loginSuccess(res) {
      if (res && res.salehistory_url) {
        this.$store.set("salehistory_url", res.salehistory_url);
      }
      this.$store.set("user", res);
      // Track successful login (signup flow)
      try {
        this.$trackLogin("signup_auto");
      } catch (e) {
        console.warn("GA4 login event error", e);
      }
      if (this.flutter) {
        await this.flutter.callHandler("setdata", "loginResponse", res);
        await this.flutter.callHandler(
          "setdata",
          "username",
          `${res?.username}`,
        );
        try {
          const isBranch =
            res["userCategoryId"] == 2 || res["usercategoryid"] == 2;
          const key = isBranch ? "branchProfile" : "customerProfile";
          await this.saveProfileToStorage(key, res);
        } catch (err) {
          console.error("errorwritesecureprofile", JSON.stringify(err));
        }
      }
      if (["2078151134", "78151134"].includes(res?.username)) {
        await this.$router.replace({ path: `/shoppings` });
      } else {
        await this.$router.replace({ path: `/` });
      }
    },
  },
};
</script>

<style lang="postcss">
@font-face {
  font-family: "NotoSansLao";
  src: url("../public/fonts/NotoSansLao-Regular.ttf") format("truetype");
}

@font-face {
  font-family: "MyriadPro";
  src: url("../public/fonts/MyriadPro-Regular.otf") format("opentype");
}

#app,
.swal2-popup,
.swal2-title,
.swal2-confirm,
.swal2-cancel {
  font-family: NotoSansLao;
}

.font-number,
.font-pro {
  font-family: MyriadPro;
}

/* Prevent SweetAlert from affecting app height */
body.swal2-shown {
  overflow: hidden !important;
  height: 100vh !important;
}

body.swal2-shown #app {
  height: 100vh !important;
  overflow: hidden !important;
}

.swal2-container,
.swal2-container-custom {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  z-index: 9999 !important;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.swal2-popup {
  position: relative !important;
  margin: 0 !important;
  top: auto !important;
  transform: none !important;
}

.modal {
  @apply fixed inset-0 bg-black bg-opacity-50 z-50;
}

.modal-background {
  @apply fixed bg-black bg-opacity-50 min-h-screen flex items-center justify-center inset-0 z-40;
}

.bgx {
  background-image: url("../public/bg.png");
  background-size: cover;
}

.bgwin {
  background-image: url("../public/bill/Background/background02.png");
  background-size: cover;
}

.bgwinamount {
  background-image: url("../public/bill/Background/Prize.svg");
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  height: 150px;
}

.bg-yellowx {
  background-image: linear-gradient(to right, #d3a300, #ffe89d);
}

.text-yellowxgradient {
  color: linear-gradient(to right, #d3a300, #ffe89d);
}

.text-yellowx {
  color: #754c24;
}

.border-yellowx {
  border-color: #f8cb2e;
}

/* Pull-to-refresh styles */
.ptr-indicator {
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: height 0.2s ease;
  background: transparent;
}

.ptr-indicator:not(.ptr-visible) {
  height: 0 !important;
}

.ptr-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.ptr-arrow-container,
.ptr-spinner-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.ptr-arrow {
  font-size: 24px;
  color: rgba(255, 255, 255, 0.9);
  transition: transform 0.2s ease;
  font-weight: bold;
}

.ptr-arrow-up {
  transform: rotate(180deg);
}

.ptr-text {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 300;
}

.ptr-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: ptr-spin 0.6s linear infinite;
}

@keyframes ptr-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ===== Floating Reload Button ===== */
.floating-reload-wrapper {
  position: fixed;
  bottom: 28px;
  right: 20px;
  z-index: 10000;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.floating-reload-btn {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.reload-glow {
  position: absolute;
  inset: -8px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(52, 211, 153, 0.45) 0%,
    rgba(16, 185, 129, 0.2) 50%,
    transparent 70%
  );
  animation: reload-glow-pulse 2s ease-in-out infinite;
  pointer-events: none;
}

.reload-icon-wrap {
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: linear-gradient(135deg, #059669 0%, #10b981 50%, #34d399 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.45), 0 2px 6px rgba(0, 0, 0, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.25);
  transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1),
    box-shadow 0.2s ease;
  animation: reload-bounce 3s ease-in-out infinite;
}

.floating-reload-wrapper:active .reload-icon-wrap {
  transform: scale(0.92);
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.35), 0 1px 3px rgba(0, 0, 0, 0.15);
}

.reload-icon {
  width: 24px;
  height: 24px;
  color: #fff;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.15));
}

.reload-label {
  font-size: 10px;
  font-weight: 600;
  color: #059669;
  letter-spacing: 0.3px;
  text-shadow: 0 0 8px rgba(16, 185, 129, 0.4);
  margin-top: 2px;
}

.reload-badge-pulse {
  position: absolute;
  top: -2px;
  right: 8px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #ef4444;
  box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
  animation: reload-badge-ping 1.5s ease-in-out infinite;
}

/* Animations */
@keyframes reload-glow-pulse {
  0%,
  100% {
    opacity: 0.6;
    transform: scale(1);
  }

  50% {
    opacity: 1;
    transform: scale(1.15);
  }
}

@keyframes reload-bounce {
  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-4px);
  }
}

@keyframes reload-badge-ping {
  0% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
  }

  70% {
    box-shadow: 0 0 0 8px rgba(239, 68, 68, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
  }
}

/* Transition */
.reload-btn-enter-active {
  animation: reload-enter 0.45s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.reload-btn-leave-active {
  animation: reload-leave 0.25s ease-in;
}

@keyframes reload-enter {
  0% {
    opacity: 0;
    transform: scale(0.3) translateY(30px);
  }

  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes reload-leave {
  0% {
    opacity: 1;
    transform: scale(1);
  }

  100% {
    opacity: 0;
    transform: scale(0.5) translateY(20px);
  }
}
</style>
